import { gql } from '@apollo/client';

/**
 * All-International Requests — the routed, multi-device sibling of the International Requests tab.
 *
 * An agent files one request with a line per device model; fulfilment ROUTES each model's units
 * to Amazon MCF, China Fulfill or Houston Fulfill; then it is quoted or invoiced with one freight
 * line per shipment. Server: apps/api/src/app/intl-all-requests/.
 *
 * Operation names match their GraphQL field names so `refetchQueries: ['intlAllRequests']` resolves.
 *
 * Two operations here create physical events: `submitIntlAllMcfOrder` (an Amazon order on HOLD)
 * and `placeIntlAllShopifyOrder` (a REAL $0 Shopify order — there is no Shopify test mode).
 */

const SHIPMENT_FIELDS = `
  id
  route
  routeLabel
  units { deviceModel quantity }
  freightMinor
  freightCurrency
  freightSource
  freightOverrideMinor
  freightOverrideCurrency
  freightOverrideBy
  freightOverrideAt
  mcfOrderId
  mcfMarketplace
  mcfShippingSpeed
  mcfSubmittedAt
  mcfSubmittedBy
  mcfReleasedAt
  mcfReleasedBy
  mcfCancelledAt
  mcfStatus
  mcfError
  shopifyOrderId
  shopifyOrderName
  shopifyAdminUrl
  shopifyLocationId
  shopifyOrderedAt
  shopifyOrderedBy
  shopifyError
  trackingNumber
  trackingCarrier
  trackingUrl
  trackedAt
  ordered
`;

const INTL_ALL_REQUEST_FIELDS = `
  id
  requestNo
  submittedBy
  submittedAt
  stripeCustomerId
  contactEmail
  contactName
  company
  vatNumber
  accountId
  hubspotContactId
  hubspotDealId
  currency
  poNumber
  shipToName
  shipToLine1
  shipToLine2
  shipToCity
  shipToRegion
  shipToPostalCode
  shipToCountry
  shipToPhone
  addressComplete
  addressMissing
  lastUpdatedBy
  lastUpdatedAt
  lines { deviceModel quantity }
  allocations { deviceModel route quantity }
  shipments { ${SHIPMENT_FIELDS} }
  routedAt
  routedBy
  stripeQuoteId
  quoteStatus
  quoteNumber
  quoteTotal
  quoteCurrency
  quoteExpiresAt
  quoteExpired
  quoteCreatedBy
  quoteCreatedAt
  quoteAcceptedBy
  quoteAcceptedAt
  quoteCanceledAt
  quoteError
  stripeInvoiceId
  invoiceNumber
  invoiceStatus
  invoiceUrl
  invoiceTotal
  invoiceAutoSendAt
  invoicePaidAt
  shopifyLiveStore
  fulfilApprovedAt
  fulfilApprovedBy
  fulfilApprovalReason
  invoiceCheckedAt
  invoiceRaisedBy
  invoiceRaisedAt
  invoiceError
  stage
`;

export const INTL_ALL_REQUESTS = gql`
  query intlAllRequests($filter: IntlAllRequestFilterInput, $pagination: PaginationInput) {
    intlAllRequests(filter: $filter, pagination: $pagination) {
      items { ${INTL_ALL_REQUEST_FIELDS} }
      total
    }
  }
`;

export const INTL_ALL_REQUEST = gql`
  query intlAllRequest($requestId: Int!) {
    intlAllRequest(requestId: $requestId) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const INTL_ALL_REQUEST_NOTES = gql`
  query intlAllRequestNotes($requestId: Int!) {
    intlAllRequestNotes(requestId: $requestId) {
      id
      staffEmail
      text
      createdAt
    }
  }
`;

/** What the request would cost as routed — per line, per shipment, and the VAT. */
export const INTL_ALL_REQUEST_PRICING = gql`
  query intlAllRequestPricing($requestId: Int!, $freightOverrides: [IntlAllFreightOverrideInput!]) {
    intlAllRequestPricing(requestId: $requestId, freightOverrides: $freightOverrides) {
      currency
      lines { deviceModel quantity unitAmount amount route routeLabel vatExempt }
      shipments {
        route
        routeLabel
        freightMinor
        freightSource
        unavailableReason
        costMinor
        speed
        fxNote
        vatExempt
        calculatedMinor
        overriddenBy
        overriddenAt
      }
      devicesTotal
      freightTotal
      total
      freightBreakdown
      vatPercent
      vatMinor
      totalIncludingVat
    }
  }
`;

/** Which models the Products List ticks allow on each kind of route. */
export const INTL_ALL_ELIGIBLE_MODELS = gql`
  query intlAllEligibleModels {
    intlAllEligibleModels {
      mcf
      shopify
    }
  }
`;

export const SUBMIT_INTL_ALL_REQUEST = gql`
  mutation submitIntlAllRequest($input: SubmitIntlAllRequestInput!) {
    submitIntlAllRequest(input: $input) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const ROUTE_INTL_ALL_REQUEST = gql`
  mutation routeIntlAllRequest($input: RouteIntlAllRequestInput!) {
    routeIntlAllRequest(input: $input) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const CREATE_INTL_ALL_QUOTE = gql`
  mutation createIntlAllQuote($input: CreateIntlAllQuoteInput!) {
    createIntlAllQuote(input: $input) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const CANCEL_INTL_ALL_QUOTE = gql`
  mutation cancelIntlAllQuote($requestId: Int!) {
    cancelIntlAllQuote(requestId: $requestId) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const CONVERT_INTL_ALL_QUOTE_TO_INVOICE = gql`
  mutation convertIntlAllQuoteToInvoice($input: ConvertIntlAllQuoteInput!) {
    convertIntlAllQuoteToInvoice(input: $input) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const RAISE_INTL_ALL_INVOICE = gql`
  mutation raiseIntlAllInvoice($input: RaiseIntlAllInvoiceInput!) {
    raiseIntlAllInvoice(input: $input) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const REFRESH_INTL_ALL_INVOICE = gql`
  mutation refreshIntlAllInvoice($requestId: Int!) {
    refreshIntlAllInvoice(requestId: $requestId) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const SUBMIT_INTL_ALL_MCF_ORDER = gql`
  mutation submitIntlAllMcfOrder($requestId: Int!) {
    submitIntlAllMcfOrder(requestId: $requestId) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const RELEASE_INTL_ALL_MCF_ORDER = gql`
  mutation releaseIntlAllMcfOrder($requestId: Int!) {
    releaseIntlAllMcfOrder(requestId: $requestId) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const CANCEL_INTL_ALL_MCF_ORDER = gql`
  mutation cancelIntlAllMcfOrder($requestId: Int!) {
    cancelIntlAllMcfOrder(requestId: $requestId) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const PLACE_INTL_ALL_SHOPIFY_ORDER = gql`
  mutation placeIntlAllShopifyOrder($requestId: Int!, $route: String!, $allowShortfall: Boolean) {
    placeIntlAllShopifyOrder(requestId: $requestId, route: $route, allowShortfall: $allowShortfall) {
      ${INTL_ALL_REQUEST_FIELDS}
    }
  }
`;

/** ROOT + FULFILLMENT. Undo the routing so the request can be corrected and routed again. */
export const UNROUTE_INTL_ALL_REQUEST = gql`
  mutation unrouteIntlAllRequest($requestId: Int!) {
    unrouteIntlAllRequest(requestId: $requestId) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

/** ROOT + SUPPORT + FULFILLMENT. Correct the shipping address, until the request is routed. */
export const UPDATE_INTL_ALL_SHIPPING_ADDRESS = gql`
  mutation updateIntlAllShippingAddress($input: UpdateIntlAllShippingAddressInput!) {
    updateIntlAllShippingAddress(input: $input) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

/** The list page's Refresh: force-read Stripe and Shopify / Amazon for the requests on screen. */
export const REFRESH_INTL_ALL_REQUESTS = gql`
  mutation refreshIntlAllRequests($requestIds: [Int!]!) {
    refreshIntlAllRequests(requestIds: $requestIds) { invoicesChecked shipmentsChecked failed }
  }
`;

/** ROOT ONLY. Approve shipping an invoiced request before payment. */
export const APPROVE_INTL_ALL_FULFILMENT = gql`
  mutation approveIntlAllFulfilment($input: ApproveIntlAllFulfilmentInput!) {
    approveIntlAllFulfilment(input: $input) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

/** ROOT ONLY. Withdraw it while nothing has been ordered on it. */
export const REVOKE_INTL_ALL_FULFILMENT_APPROVAL = gql`
  mutation revokeIntlAllFulfilmentApproval($requestId: Int!) {
    revokeIntlAllFulfilmentApproval(requestId: $requestId) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

/** ROOT + FULFILLMENT. Save a freight figure for one route over the system's; null drops it. */
export const SET_INTL_ALL_FREIGHT_OVERRIDE = gql`
  mutation setIntlAllFreightOverride($input: SetIntlAllFreightOverrideInput!) {
    setIntlAllFreightOverride(input: $input) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

/** ROOT ONLY. Link the order found in Shopify, or clear the claim so Create can be pressed again. */
export const CLEAR_INTL_ALL_SHOPIFY_CLAIM = gql`
  mutation clearIntlAllShopifyClaim($input: ClearIntlAllShopifyClaimInput!) {
    clearIntlAllShopifyClaim(input: $input) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const REFRESH_INTL_ALL_TRACKING = gql`
  mutation refreshIntlAllTracking($requestId: Int!) {
    refreshIntlAllTracking(requestId: $requestId) { ${INTL_ALL_REQUEST_FIELDS} }
  }
`;

export const ADD_INTL_ALL_REQUEST_NOTE = gql`
  mutation addIntlAllRequestNote($requestId: Int!, $text: String!) {
    addIntlAllRequestNote(requestId: $requestId, text: $text) {
      id
      staffEmail
      text
      createdAt
    }
  }
`;

// ── Fulfillment inventory (ops sheet + Amazon) ──────────────────────────────────────────────

const INTL_ALL_INVENTORY_LEVEL_FIELDS = `
  route
  routeLabel
  deviceModel
  onHand
  routedNotOrdered
  error
`;

/** China / Houston on hand, from the ops inventory sheet. `requestId` leaves that request out of "routed, not ordered". */
export const INTL_ALL_INVENTORY = gql`
  query intlAllInventory($requestId: Int) {
    intlAllInventory(requestId: $requestId) { ${INTL_ALL_INVENTORY_LEVEL_FIELDS} }
  }
`;

/** Amazon's live fulfillable units for the marketplace(s) that would ship this request. */
export const INTL_ALL_MCF_STOCK = gql`
  query intlAllMcfStock($requestId: Int!) {
    intlAllMcfStock(requestId: $requestId) {
      marketplace
      marketplaceLabel
      deviceModel
      fulfillable
      error
    }
  }
`;
